import * as React from 'react';
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Dismiss20Regular, Sparkle20Filled } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { findAnnouncement } from '../../announcements/announcements';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { clearCurrentAnnouncement } from '../../redux/features/announcements/announcementsSlice';
import { AnnouncementsService } from '../../libs/services/AnnouncementsService';

const useClasses = makeStyles({
    root: {
        boxSizing: 'border-box',
        margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        padding: tokens.spacingHorizontalM,
        backgroundColor: tokens.colorBrandBackground2,
        borderLeft: `4px solid ${tokens.colorBrandBackground}`,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalS,
    },
    icon: {
        color: tokens.colorBrandForeground1,
        flexShrink: 0,
        marginTop: '2px',
    },
    titleColumn: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
    },
    body: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
    },
    dismiss: {
        flexShrink: 0,
    },
    actions: {
        display: 'flex',
        flexDirection: 'row',
        gap: tokens.spacingHorizontalS,
        flexWrap: 'wrap',
    },
    actionsRight: {
        marginLeft: 'auto',
    },
});

export const AnnouncementBanner: React.FC = () => {
    const classes = useClasses();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const auth = useAuth();
    const token = auth.user?.access_token;

    const currentId = useAppSelector((state: RootState) => state.announcements.currentId);
    const featureFlagOn = useAppSelector((state: RootState) => state.app.serviceInfo.featureFlags.AnnouncementsEnabled);
    const selectedChatId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const isStreaming = useAppSelector((state: RootState) => {
        if (!selectedChatId) {
            return false;
        }
        // selectedChatId can briefly be set before the conversation hydrates (or after a
        // fast-switch). The Record<string, ChatState> type is loose about missing keys,
        // so cast to allow the optional-chain guard at runtime.
        const conv = state.conversations.conversations[selectedChatId] as
            (typeof state.conversations.conversations)[string] | undefined;
        return Boolean(conv?.botResponseStatus);
    });

    const announcement = currentId ? findAnnouncement(currentId) : undefined;
    const isVisible = Boolean(featureFlagOn && announcement && !isStreaming);

    // Returning undefined when the token is missing keeps "service exists ⇒ token exists"
    // in the type system. The banner is also gated behind isVisible/announcement below,
    // so dismiss/snooze handlers can rely on `service` being present once those gates pass.
    const service = React.useMemo(() => (token ? new AnnouncementsService(token) : undefined), [token]);
    const [expanded, setExpanded] = React.useState(false);
    const [impressionRegistered, setImpressionRegistered] = React.useState<string | undefined>(undefined);

    // Reset the expand state when the user moves to a different announcement so a new
    // banner does not inherit the expanded view from the previous one.
    React.useEffect(() => {
        setExpanded(false);
    }, [currentId]);

    // Register impression once per displayed announcement. Only fires when the banner is
    // actually rendered — otherwise we'd set LastShownAt and trigger the 7-day cap for
    // something the user never saw (e.g. while streaming or flag off).
    React.useEffect(() => {
        if (!isVisible || !currentId || impressionRegistered === currentId || !service) {
            return;
        }
        void service.registerImpressionAsync(currentId).catch((err: unknown) => {
            console.warn('AnnouncementBanner: impression failed', err);
        });
        setImpressionRegistered(currentId);
    }, [isVisible, currentId, impressionRegistered, service]);

    if (!isVisible || !announcement || !service) {
        return null;
    }

    const handleExpand = () => {
        setExpanded(true);
        void service.markExpandedAsync(announcement.id).catch((err: unknown) => {
            console.warn('AnnouncementBanner: expand failed', err);
        });
    };

    const handleDismiss = () => {
        void service.markSeenAsync(announcement.id).catch((err: unknown) => {
            console.warn('AnnouncementBanner: dismiss failed', err);
        });
        dispatch(clearCurrentAnnouncement());
    };

    const handleSnooze = () => {
        void service.markSnoozedAsync(announcement.id).catch((err: unknown) => {
            console.warn('AnnouncementBanner: snooze failed', err);
        });
        dispatch(clearCurrentAnnouncement());
    };

    return (
        <div
            className={classes.root}
            role="region"
            aria-label={t('announcements.banner.aria')}
            aria-live="polite"
            data-testid="announcement-banner"
        >
            <div className={classes.header}>
                <Sparkle20Filled className={classes.icon} aria-hidden />
                <div className={classes.titleColumn}>
                    {/* Translations render as plain-text children — React escapes any HTML a
                        translator might embed (so e.g. `<b>foo</b>` shows literally, which
                        is the safe default). If this banner ever grows Markdown / rich-text
                        support, route the output through a sanitizer (DOMPurify or similar)
                        before rendering; do NOT swap in dangerouslySetInnerHTML. */}
                    <span className={classes.title}>{t(announcement.titleKey)}</span>
                    {expanded && <span className={classes.body}>{t(announcement.bodyKey)}</span>}
                </div>
                <Button
                    className={classes.dismiss}
                    appearance="subtle"
                    icon={<Dismiss20Regular />}
                    aria-label={t('announcements.banner.dismiss')}
                    onClick={handleDismiss}
                />
            </div>
            <div className={classes.actions}>
                {!expanded && (
                    <Button appearance="primary" size="small" onClick={handleExpand}>
                        {t('announcements.banner.expand')}
                    </Button>
                )}
                <div className={mergeClasses(classes.actionsRight)}>
                    <Button appearance="subtle" size="small" onClick={handleSnooze}>
                        {t('announcements.banner.snooze')}
                    </Button>
                    {expanded && (
                        <Button appearance="primary" size="small" onClick={handleDismiss}>
                            {t('announcements.banner.dismiss')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
